import { useState, useRef, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { postEventStream } from "@/lib/stream";
import { useLang } from "@/App";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import NorthIndianChart from "@/components/astrology/NorthIndianChart";
import PlanetaryPositions from "@/components/astrology/PlanetaryPositions";
import DashaPeriods from "@/components/astrology/DashaPeriods";
import {
  Send, Loader2, RotateCcw, Sparkles, Heart, Briefcase,
  Baby, Home, TrendingUp, FileDown, User, Users, Save, MapPin, Layers,
  BookOpen, Coins, ShieldAlert, Gem,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LALKITAB_REPORT = [
  { key: "essence", label: "Chart Essence" },
  { key: "planets", label: "Planet Analysis" },
  { key: "houses_karma", label: "Houses & Karma" },
  { key: "dosh_life", label: "Doshas & Life Areas" },
  { key: "remedies", label: "Remedies" },
  { key: "dashboard", label: "Priority Dashboard" },
];

const STEPS = {
  MODE_SELECT: "MODE_SELECT",
  FORM: "FORM",
  FORM2: "FORM2",
  CALCULATING: "CALCULATING",
  RESULTS: "RESULTS",
  CHAT: "CHAT",
};

function renderMarkdown(text) {
  if (!text) return null;
  const lines = text.split("\n");
  const elements = [];
  let listItems = [];
  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(<ul key={`ul-${elements.length}`} className="list-disc pl-5 mb-2">{listItems}</ul>);
      listItems = [];
    }
  };
  lines.forEach((line, i) => {
    if (line.startsWith("## ")) { flushList(); elements.push(<h2 key={i}>{line.slice(3)}</h2>); }
    else if (line.startsWith("### ")) { flushList(); elements.push(<h3 key={i}>{line.slice(4)}</h3>); }
    else if (line.startsWith("- ") || line.startsWith("* ")) { listItems.push(<li key={i}>{fmtInline(line.slice(2))}</li>); }
    else if (line.match(/^\d+\.\s/)) { listItems.push(<li key={i}>{fmtInline(line.replace(/^\d+\.\s/, ""))}</li>); }
    else if (line.trim() === "") { flushList(); elements.push(<div key={i} className="h-2" />); }
    else { flushList(); elements.push(<p key={i}>{fmtInline(line)}</p>); }
  });
  flushList();
  return <div className="markdown-content">{elements}</div>;
}

function fmtInline(text) {
  return text.split(/(\*\*.*?\*\*)/g).map((p, i) =>
    p.startsWith("**") && p.endsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : p
  );
}

// Reveals an answer smoothly as it arrives, so text flows in instead of
// appearing all at once, and keeps the start of the answer in view: while the
// answer is shorter than the screen it follows the newest line, and once its
// first line reaches the top it stays put so the reader starts at the beginning.
function LiveAnswer({ text, live, followRef, children }) {
  const [shown, setShown] = useState(live ? 0 : text.length);
  const shownRef = useRef(shown);
  const textRef = useRef(text);
  const elRef = useRef(null);
  textRef.current = text;

  useEffect(() => {
    if (shownRef.current >= text.length) {
      if (shownRef.current > text.length) { shownRef.current = text.length; setShown(text.length); }
      return;
    }
    let raf;
    const step = () => {
      const target = textRef.current.length;
      const backlog = target - shownRef.current;
      if (backlog <= 0) return;
      let next = shownRef.current + Math.max(2, Math.ceil(backlog / 18));
      // Finish the current word so half-words don't flicker.
      const space = textRef.current.indexOf(" ", next);
      if (space !== -1 && space - next < 12) next = space;
      shownRef.current = Math.min(target, next);
      setShown(shownRef.current);
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [text]);

  useEffect(() => {
    const el = elRef.current;
    if (!live || !el || !followRef?.current) return;
    const viewport = el.closest("[data-radix-scroll-area-viewport]");
    if (!viewport) return;
    const offset = el.getBoundingClientRect().top - viewport.getBoundingClientRect().top - 12;
    if (offset > 1) viewport.scrollTop += offset;
  }, [shown, live, followRef]);

  let visible = text.slice(0, shown);
  if ((visible.match(/\*\*/g) || []).length % 2) visible += "**";
  const typing = live && shown < text.length;

  return (
    <div ref={elRef} className="bot-bubble rounded-2xl rounded-tl-sm px-5 py-4 max-w-[90%]">
      {visible ? renderMarkdown(visible) : null}
      {typing && <span className="live-caret" aria-hidden="true" />}
      {children}
    </div>
  );
}

const THINKING_LINES = [
  "Reading your Lagna and planetary houses",
  "Checking your current Dasha period",
  "Looking at the Navamsha chart",
  "Weighing planetary aspects and transits",
  "Putting your reading into words",
];

function ThinkingLine() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((n) => (n + 1) % THINKING_LINES.length), 2800);
    return () => clearInterval(t);
  }, []);
  return (
    <p key={i} className="text-sm text-slate-400 animate-fade-in flex items-center gap-2">
      <Sparkles className="h-3.5 w-3.5 text-amber-400 animate-pulse-slow shrink-0" />
      {THINKING_LINES[i]}…
    </p>
  );
}

export default function ChatPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLang();
  const { user } = useAuth();
  const [mode, setMode] = useState(null);
  const [messages, setMessages] = useState([]);
  const [step, setStep] = useState(STEPS.MODE_SELECT);
  const [inputValue, setInputValue] = useState("");
  const [birthDetails, setBirthDetails] = useState({});
  const [birthDetails2, setBirthDetails2] = useState({});
  const [kundliData, setKundliData] = useState(null);
  const [kundliData2, setKundliData2] = useState(null);
  const [compatibility, setCompatibility] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  const initialized = useRef(false);
  const autoScrollRef = useRef(true);
  // Whether the live answer may still move the screen; any manual scroll stops it.
  const followRef = useRef(true);

  const scroll = useCallback(() => {
    if (autoScrollRef.current) {
      setTimeout(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    }
  }, []);

  const addBot = useCallback((text, type = "text", data = null) => {
    setMessages((prev) => [...prev, { id: Date.now() + Math.random(), sender: "bot", text, type, data }]);
  }, []);

  const addUser = useCallback((text) => {
    autoScrollRef.current = true; // Only auto-scroll when user sends a message
    setMessages((prev) => [...prev, { id: Date.now() + Math.random(), sender: "user", text, type: "text" }]);
  }, []);

  useEffect(() => {
    if (location.state?.restoreSession) {
      const s = location.state.restoreSession;
      setMode(s.mode || "personal");
      setKundliData(s.kundli_data || null);
      setKundliData2(s.kundli_data_2 || null);

      // Restore messages — re-attach chart data to special message types
      const restoredMessages = (s.messages || []).map(m => {
        const base = { ...m, id: m.id || Date.now() + Math.random() };
        if (m.type === "results" && !m.data && s.kundli_data) {
          return { ...base, data: s.kundli_data };
        }
        if (m.type === "compatibility" && !m.data && s.kundli_data && s.kundli_data_2) {
          return { ...base, data: { kundli1: s.kundli_data, kundli2: s.kundli_data_2, compatibility: null } };
        }
        return base;
      });
      setMessages(restoredMessages);
      setStep(STEPS.CHAT);
      setSessionId(s.id || null);
      setSaved(true);
      initialized.current = true;
      window.history.replaceState({}, "");

      // Recalculate compatibility scores if in compatibility mode
      if (s.mode === "compatibility" && s.kundli_data && s.kundli_data_2) {
        axios.post(`${API}/kundli/compatibility`, { boy_kundli: s.kundli_data, girl_kundli: s.kundli_data_2 })
          .then(res => {
            if (res.data.success) {
              setCompatibility(res.data.data);
              // Patch the compatibility message with real scores
              setMessages(prev => prev.map(m =>
                m.type === "compatibility" ? { ...m, data: { kundli1: s.kundli_data, kundli2: s.kundli_data_2, compatibility: res.data.data } } : m
              ));
            }
          }).catch(() => {});
      }
    }
  }, [location.state]);

  useEffect(() => {
    if (autoScrollRef.current) scroll();
  }, [messages, scroll]);

  // Reset to mode selection when user navigates "Home" (location.key changes without restoreSession)
  const locationKey = useRef(location.key);
  useEffect(() => {
    if (location.key !== locationKey.current) {
      locationKey.current = location.key;
      if (!location.state?.restoreSession && step !== STEPS.MODE_SELECT) {
        setMessages([]); setStep(STEPS.MODE_SELECT); setMode(null);
        setBirthDetails({}); setBirthDetails2({});
        setKundliData(null); setKundliData2(null); setCompatibility(null);
        setSaved(false); setSessionId(null); setLoading(false);
      }
    }
  }, [location.key, location.state, step]);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
  }, []);

  // --- API calls ---
  const generateKundli = async (details) => {
    setLoading(true);
    addBot("Aligning the celestial bodies...", "loading");
    const payload = {
      name: details.name,
      date_of_birth: details.date_of_birth,
      time_of_birth: details.time_of_birth,
      place_of_birth: details.place_of_birth,
    };
    if (details.latitude != null) {
      payload.latitude = details.latitude;
      payload.longitude = details.longitude;
      payload.timezone_str = details.timezone_str;
    }
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        const res = await axios.post(`${API}/kundli/generate`, payload, { timeout: 120000 });
        if (res.data.success) {
          setMessages((prev) => prev.filter((m) => m.type !== "loading"));
          return res.data.data;
        }
      } catch (e) {
        if ((e.response?.status === 502 || e.response?.status === 503) && attempt < 2) {
          await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
          continue;
        }
        const errMsg = e.response?.data?.detail || "Could not find this location. Try selecting from the search suggestions.";
        setMessages((prev) => prev.filter((m) => m.type !== "loading"));
        addBot(`I had trouble generating the chart: ${errMsg}`);
        setLoading(false);
        return null;
      }
    }
    setMessages((prev) => prev.filter((m) => m.type !== "loading"));
    addBot("The chart generation timed out. Please try again.");
    setLoading(false);
    return null;
  };

  const startPersonalFlow = () => {
    setMode("personal");
    setStep(STEPS.FORM);
    addBot("Namaste! Please fill in the birth details below to generate your Kundli.");
  };

  const startCompatFlow = () => {
    setMode("compatibility");
    setStep(STEPS.FORM);
    addBot("Namaste! Let's check marriage compatibility. Please fill in the Man's birth details.");
  };

  const startLalKitabFlow = () => {
    setMode("lalkitab");
    setStep(STEPS.FORM);
    addBot("Namaste! Enter your birth details below. I'll analyze your chart through the practical wisdom of Lal Kitab — house by house — and give you safe, actionable remedies (upaay).");
  };

  const handlePersonalGenerate = async (details) => {
    const data = await generateKundli(details);
    if (!data) { setStep(STEPS.FORM); return; }
    setKundliData(data);
    addBot("Your Kundli has been created! Ask me anything about your life — love, marriage, career, finances, or health.", "results", data);
    setStep(STEPS.CHAT);
    setLoading(false);
  };

  const runLalKitabReport = async (k) => {
    const allowed = await checkReadingLimit();
    if (!allowed) { setLoading(false); return; }
    setLoading(true);
    autoScrollRef.current = false;
    const msgId = Date.now() + Math.random();
    setMessages((prev) => [...prev, { id: msgId, sender: "bot", text: "", type: "growing", live: true, sectionsTotal: LALKITAB_REPORT.length, sectionsDone: 0 }]);
    followRef.current = true;
    let accumulated = "";
    for (let i = 0; i < LALKITAB_REPORT.length; i++) {
      let result = null;
      try {
        const r = await axios.post(`${API}/kundli/lalkitab`, { kundli_data: k, section: LALKITAB_REPORT[i].key, language }, { timeout: 60000 });
        if (r.data.success) result = r.data.interpretation;
      } catch { /* skip section on error */ }
      if (result) accumulated += (accumulated ? "\n\n" : "") + result;
      else if (!accumulated) accumulated = "The reading is taking a moment. Please ask a question below to continue.";
      setMessages((prev) => prev.map((m) =>
        m.id === msgId
          ? { ...m, text: accumulated, sectionsDone: i + 1, type: i === LALKITAB_REPORT.length - 1 ? "interpretation" : "growing" }
          : m
      ));
    }
    setLoading(false);
  };

  const handleLalKitabGenerate = async (details) => {
    const data = await generateKundli(details);
    if (!data) { setStep(STEPS.FORM); return; }
    setKundliData(data);
    addBot("Your Lal Kitab chart is ready! Generating your house-based analysis and remedies — this streams in section by section.", "results", data);
    setStep(STEPS.CHAT);
    await runLalKitabReport(data);
  };

  const handleCompatGenerate = async (d1, d2) => {
    const data1 = await generateKundli(d1);
    if (!data1) { setStep(STEPS.FORM); return; }
    setKundliData(data1);
    addBot(`${d1.name}'s chart is ready. Generating ${d2.name}'s chart...`, "loading");
    const data2 = await generateKundli(d2);
    if (!data2) { setStep(STEPS.FORM2); return; }
    setKundliData2(data2);
    setMessages((prev) => prev.filter((m) => m.type !== "loading"));
    // Calculate Ashta Koot compatibility
    try {
      const compatRes = await axios.post(`${API}/kundli/compatibility`, { boy_kundli: data1, girl_kundli: data2 });
      if (compatRes.data.success) {
        setCompatibility(compatRes.data.data);
        addBot("Compatibility analysis complete!", "compatibility", { kundli1: data1, kundli2: data2, compatibility: compatRes.data.data });
      }
    } catch {
      addBot(`Both charts are ready! Ask me about compatibility.`, "results", data1);
    }
    setStep(STEPS.CHAT);
    setLoading(false);
  };

  // --- Sub-query splitter: breaks broad questions into sequential detailed sections ---
  const splitQuery = (question) => {
    const q = question.toLowerCase();
    if (mode === "lalkitab") return null;
    const isCompat = mode === "compatibility" && kundliData2;

    if (isCompat) {
      if (q.includes("complete") || q.includes("full") || q.includes("overall") || q.includes("everything") || q.includes("compatibility analysis")) {
        return [
          "Analyze the Ashta Koot Milan scores in detail. Explain what each Koot score means for this specific couple — which ones are strong and which are weak. Reference the actual nakshatra, rashi, yoni, gana, nadi values. Start with '## Ashta Koot Analysis'",
          "Give a detailed analysis of love, romance, emotional bonding and affection. Analyze Venus placement in both D-1 and D-9, 7th house lords, Moon signs. Is there risk of affairs? IMPORTANT: Name specific Dasha periods (with dates) when the relationship will be strongest and when it may face challenges. Start with '## Love, Romance & Affection'",
          "Analyze physical compatibility (Yoni Koot, Mars/Venus, 8th house) and children prospects (5th house, Jupiter, Nadi Dosha). Give SPECIFIC timing — which Dasha/Antar Dasha periods (with dates) are best for conception and which to avoid. Start with '## Physical Compatibility & Children'",
          "Analyze career, finances, property impact of this marriage. Check 2nd, 7th, 10th, 11th houses. Give SPECIFIC date ranges from both Dasha timelines for financial peaks and challenges. Start with '## Career, Money & Property'",
          "Check Manglik Dosha (Mars in 1/4/7/8/12) in both charts. List ALL risk factors with SPECIFIC Dasha periods (dates) when separation risk is highest and when it's lowest. Start with '## Manglik Check & Risk Factors'",
          "Provide specific remedies with exact mantra text, gemstone details (following safety rules), donations, fasting days, pujas. For each remedy, state WHEN to start based on current Dasha. Start with '## Remedies for the Couple'",
        ];
      }
    }

    if (!isCompat) {
      if (q.includes("complete") || q.includes("full") || q.includes("everything") || q.includes("overall") || q.includes("life") || q.includes("analyze my kundli") || q.includes("analyze my chart")) {
        return [
          "Analyze personality, core nature, strengths and challenges from D-1 ascendant, lord placement, key conjunctions. Name the specific planets and houses that shape their character. Start with '## Personality & Nature'",
          "Detailed career and financial analysis. Name the EXACT Dasha/Antar Dasha periods (with dates) for career growth, promotions, best investment periods, and periods to be cautious. Check 10th, 2nd, 11th houses and Dhana Yogas. Property timing from 4th house. Start with '## Career, Finances & Property'",
          "Detailed love and marriage analysis. Check 7th house in D-1 and D-9, Venus placement, Navamsha ascendant. Give SPECIFIC date ranges from Antar Dasha timeline for when marriage is most likely. What kind of partner? Any Manglik concern? Start with '## Love & Marriage'",
          "Children prospects from 5th house, Jupiter. Health analysis from 6th/8th houses. Name SPECIFIC Dasha periods (with dates) that need health precautions and which are safe. Start with '## Children & Health'",
          "Current Maha Dasha and exact Antar Dasha predictions. Month-by-month outlook for the next 2 years — name each Antar Dasha transition with its date and what it brings. Key opportunities and challenges with specific timing. Start with '## Current Period & Predictions'",
          "Comprehensive remedies — gemstones (safety rules), mantras with exact text and count, donations, fasting. State WHEN to start each remedy based on current Antar Dasha transition. Start with '## Remedies'",
        ];
      }
    }

    // Check for multi-topic questions
    const topics = [];
    if (q.includes("love") || q.includes("marriage") || q.includes("partner") || q.includes("relationship")) topics.push("love");
    if (q.includes("career") || q.includes("job") || q.includes("work") || q.includes("profession")) topics.push("career");
    if (q.includes("money") || q.includes("financ") || q.includes("wealth") || q.includes("property")) topics.push("money");
    if (q.includes("child") || q.includes("kids") || q.includes("baby") || q.includes("progeny")) topics.push("children");
    if (q.includes("health")) topics.push("health");

    if (topics.length >= 2) {
      const sections = [];
      if (topics.includes("love")) sections.push("Give a detailed love and marriage analysis. Check 7th house D-1 and D-9, Venus, current dasha impact. Start with '## Love & Marriage'");
      if (topics.includes("career")) sections.push("Give a detailed career analysis. 10th house lord, current dasha, upcoming opportunities. Start with '## Career'");
      if (topics.includes("money")) sections.push("Give a detailed financial and property analysis. 2nd, 4th, 11th houses. Start with '## Finances & Property'");
      if (topics.includes("children")) sections.push("Give a detailed children prospects analysis. 5th house, Jupiter, putra dosha check. Start with '## Children'");
      if (topics.includes("health")) sections.push("Give a detailed health analysis. 6th, 8th houses, current vulnerabilities. Start with '## Health'");
      return sections;
    }

    return null;
  };

  const askSingleQuery = async (question, fastModel = false, onDelta = null) => {
    if (mode === "lalkitab") {
      try {
        const r = await axios.post(`${API}/kundli/lalkitab`, { kundli_data: kundliData, question, language }, { timeout: 60000 });
        if (r.data.success) return r.data.interpretation;
      } catch { /* fall through */ }
      return null;
    }
    const payload = { kundli_data: kundliData, question, language, fast: fastModel, user_mode: user?.mode || "simple" };
    if (mode === "compatibility" && kundliData2) {
      payload.kundli_data_2 = kundliData2;
      payload.compatibility = compatibility;
    }
    if (onDelta) {
      try {
        const result = await postEventStream(`${API}/kundli/interpret/stream`, payload, {
          onDelta,
          onReset: () => onDelta(null),
        });
        return result?.success ? result.interpretation : null;
      } catch { /* stream unavailable: use the regular request */ }
    }
    try {
      const r = await axios.post(`${API}/kundli/interpret`, payload, { timeout: 120000 });
      if (r.data.success) return r.data.interpretation;
    } catch { /* fall through */ }
    return null;
  };

  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeTimeLeft, setUpgradeTimeLeft] = useState("");

  const checkReadingLimit = async () => {
    try {
      const res = await axios.post(`${API}/user/use-reading`);
      if (res.data.success) return true;
      // Blocked — show upgrade prompt
      setUpgradeTimeLeft(res.data.next_free_in || "24h");
      setShowUpgradePrompt(true);
      addBot(`You've used your 3 free readings for today. Next free reading in **${res.data.next_free_in || "~24 hours"}**. Purchase more readings to continue.`, "text");
      return false;
    } catch {
      return true; // allow on error
    }
  };

  const askFollowUp = async (question) => {
    if (!kundliData) return;

    // Check reading limit before making AI call
    const allowed = await checkReadingLimit();
    if (!allowed) {
      setLoading(false);
      return;
    }

    setLoading(true);
    autoScrollRef.current = false;

    const subQueries = splitQuery(question);
    const sections = subQueries || [question];
    const isSplit = !!subQueries;

    // Always use growing message pattern — unified experience
    const msgId = Date.now() + Math.random();
    setMessages((prev) => [...prev, { id: msgId, sender: "bot", text: "", type: "growing", live: true, sectionsTotal: sections.length, sectionsDone: 0 }]);
    followRef.current = true;

    let accumulated = "";
    for (let i = 0; i < sections.length; i++) {
      let partial = "";
      const onDelta = (piece) => {
        partial = piece === null ? "" : partial + piece;
        const text = accumulated + (accumulated && partial ? "\n\n" : "") + partial;
        setMessages((prev) => prev.map((m) => (m.id === msgId ? { ...m, text } : m)));
      };
      const result = await askSingleQuery(sections[i], isSplit, onDelta);
      if (result) {
        accumulated += (accumulated ? "\n\n" : "") + result;
      } else if (!accumulated) {
        accumulated = "The stars are busy right now. Please try again.";
      }
      setMessages((prev) => prev.map((m) =>
        m.id === msgId
          ? { ...m, text: accumulated, sectionsDone: i + 1, type: i === sections.length - 1 ? "interpretation" : "growing" }
          : m
      ));
    }
    setLoading(false);
    // Don't re-enable auto-scroll — let user control their scroll position
  };

  const handleFormSubmit = (formData) => {
    setBirthDetails(formData);
    addUser(`${formData.name} | ${formData.date_of_birth} | ${formData.time_of_birth} | ${formData.place_of_birth}`);
    if (mode === "compatibility") {
      setStep(STEPS.FORM2);
      addBot("Great! Now please fill in the Woman's birth details.");
    } else if (mode === "lalkitab") {
      setStep(STEPS.CALCULATING);
      handleLalKitabGenerate(formData);
    } else {
      setStep(STEPS.CALCULATING);
      handlePersonalGenerate(formData);
    }
  };

  const handleForm2Submit = (formData) => {
    setBirthDetails2(formData);
    addUser(`${formData.name} | ${formData.date_of_birth} | ${formData.time_of_birth} | ${formData.place_of_birth}`);
    setStep(STEPS.CALCULATING);
    handleCompatGenerate(birthDetails, formData);
  };

  const handleSend = () => {
    const text = inputValue.trim();
    if (!text || loading) return;
    setInputValue("");
    addUser(text);
    setTimeout(() => inputRef.current?.focus(), 50);
    if (step === STEPS.CHAT || step === STEPS.RESULTS) {
      askFollowUp(text);
    }
  };

  const handleSaveSession = async () => {
    if (!kundliData || saved) return;
    try {
      const payload = {
        mode, kundli_data: kundliData, kundli_data_2: kundliData2,
        messages: messages.filter(m => m.type !== "loading").map(m => ({ sender: m.sender, text: m.text || "", type: m.type })),
        interpretation: "", remedies: "",
      };
      const res = await axios.post(`${API}/kundli/save`, payload);
      if (res.data.success) { setSaved(true); setSessionId(res.data.id); toast.success("Session saved!"); }
    } catch { toast.error("Failed to save session"); }
  };

  const handleRestart = () => {
    setMessages([]); setStep(STEPS.MODE_SELECT); setMode(null);
    setBirthDetails({}); setBirthDetails2({});
    setKundliData(null); setKundliData2(null); setCompatibility(null);
    setSaved(false); setSessionId(null); setLoading(false);
  };

  // === PDF EXPORT SYSTEM ===
  const [showExportMenu, setShowExportMenu] = useState(false);

  const buildChartSVG = (houses, title) => {
    if (!houses) return "";
    const LAYOUT = {
      1:{sx:210,sy:35,cx:210,cy:110},2:{sx:75,sy:28,cx:112,cy:65},3:{sx:22,sy:62,cx:52,cy:112},
      4:{sx:32,sy:210,cx:115,cy:210},5:{sx:22,sy:358,cx:52,cy:308},6:{sx:75,sy:392,cx:112,cy:358},
      7:{sx:210,sy:388,cx:210,cy:312},8:{sx:345,sy:392,cx:308,cy:358},9:{sx:398,sy:358,cx:368,cy:308},
      10:{sx:388,sy:210,cx:305,cy:210},11:{sx:398,sy:62,cx:368,cy:112},12:{sx:345,sy:28,cx:308,cy:65},
    };
    let svg = "";
    Object.entries(houses).forEach(([num, house]) => {
      const h = parseInt(num); const L = LAYOUT[h]; if (!L) return;
      const planets = house.planets || [];
      const dy = [1,4,7,10].includes(h) ? 18 : 15;
      const fs = [1,4,7,10].includes(h) ? 12 : 10;
      const totalH = Math.max(0, (planets.length - 1) * dy);
      const baseY = L.cy - totalH / 2;
      svg += `<text x="${L.sx}" y="${L.sy}" fill="#9C8560" font-size="11" font-weight="600" text-anchor="middle" dominant-baseline="central" opacity="0.7">${house.sign?.number || ""}</text>`;
      planets.forEach((p, i) => {
        const y = baseY + i * dy;
        svg += `<text x="${L.cx}" y="${y}" fill="#333" font-size="${fs}" font-weight="700" text-anchor="middle" dominant-baseline="central" font-family="monospace">${p.abbreviation}${p.is_retrograde?"*":""}${p.is_exalted?"\u2191":""}${p.is_debilitated?"\u2193":""}</text>`;
      });
    });
    return `<div style="text-align:center;margin:10px 0"><p style="font-size:13px;color:#4a3728;font-weight:bold;margin-bottom:6px">${title}</p>
<svg viewBox="0 0 420 420" width="100%" style="max-width:520px;border:2px solid #C8963E;border-radius:8px;background:#FDF6E8">
<rect x="4" y="4" width="412" height="412" fill="none" stroke="#C8963E" stroke-width="2" rx="3"/>
<rect x="9" y="9" width="402" height="402" fill="none" stroke="#C8963E" stroke-width="1" rx="2"/>
<line x1="10" y1="10" x2="410" y2="410" stroke="#C8963E" stroke-width="1.5"/>
<line x1="410" y1="10" x2="10" y2="410" stroke="#C8963E" stroke-width="1.5"/>
<polygon points="210,10 10,210 210,410 410,210" fill="none" stroke="#C8963E" stroke-width="1.5"/>
<polygon points="203,12 217,12 210,24" fill="#C8963E"/>
${svg}</svg></div>`;
  };

  const buildPersonCharts = (k, label) => {
    if (!k) return "";
    return `
      <div style="text-align:center;margin:10px 0">
        <p style="font-size:13px;color:#4a3728;font-weight:bold;margin-bottom:6px">${label} — D-1 Rashi</p>
        ${buildChartSVG(k.houses, "")}
      </div>
      ${k.navamsha?.houses ? `
      <div class="pb"></div>
      <div style="text-align:center;margin:10px 0">
        <p style="font-size:13px;color:#4a3728;font-weight:bold;margin-bottom:6px">${label} — D-9 Navamsha</p>
        ${buildChartSVG(k.navamsha.houses, "")}
      </div>` : ""}`;
  };

  const buildPlanetTable = (k, isExpert) => {
    if (!k?.planets) return "";
    const rows = Object.entries(k.planets).map(([n, p]) => {
      const st = [];
      if (p.is_retrograde) st.push('<b style="color:#c00">R</b>');
      if (p.is_exalted) st.push('<b style="color:#059669">Exlt</b>');
      if (p.is_debilitated) st.push('<b style="color:#d97706">Deb</b>');
      if (p.is_combust) st.push('<span style="color:#dc2626">Cmb</span>');
      if (p.is_vargottama) st.push('<b style="color:#7c3aed">Var</b>');
      if (isExpert) {
        return `<tr><td><b>${n}</b></td><td>${p.rashi?.name} <small>(${p.rashi?.hindi})</small></td><td style="font-family:monospace">${p.degree_string}</td><td>${p.nakshatra?.name} <small>P${p.nakshatra?.pada}</small></td><td>${st.join(" ") || "-"}</td></tr>`;
      }
      return `<tr><td><b>${n}</b></td><td>${p.rashi?.name}</td><td>${st.join(" ") || "Normal"}</td></tr>`;
    }).join("");
    if (isExpert) {
      return `<table><tr><th>Planet</th><th>Sign</th><th>Degree</th><th>Nakshatra</th><th>Status</th></tr>${rows}</table>`;
    }
    return `<table><tr><th>Planet</th><th>Zodiac Sign</th><th>Status</th></tr>${rows}</table>`;
  };

  const buildReadingSummary = () => {
    return messages.filter(m => m.type === "interpretation" && m.sender === "bot" && m.text)
      .map(m => (m.text || "").replace(/\n/g, "<br>").replace(/## (.*?)(<br>|$)/g, '<h3 style="color:#b8860b;margin:12px 0 4px;font-family:Cinzel,serif">$1</h3>').replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>"))
      .join('<hr style="border:none;border-top:1px solid #e0d8c8;margin:20px 0">');
  };

  const handleExportPDF = async (type) => {
    const k = kundliData;
    if (!k) return;
    setShowExportMenu(false);
    const isExpert = type === "expert";
    const isCompat = mode === "compatibility" && kundliData2;
    const name1 = k.name || "Unknown";
    const name2 = kundliData2?.name || "";
    const title = isCompat ? `${name1} & ${name2}` : name1;
    const readingHtml = buildReadingSummary();

    // Open window IMMEDIATELY (before any async) to avoid popup blocker
    const w = window.open("", "_blank");
    if (!w) { toast.error("Please allow popups for PDF export"); return; }
    w.document.write(`<html><body style="font-family:sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;color:#666"><p>Generating ${isExpert ? "expert" : "client"} report...</p></body></html>`);

    // For CLIENT mode: generate a simplified AI summary
    let clientSummary = "";
    if (!isExpert && readingHtml) {
      try {
        const rawText = messages.filter(m => m.type === "interpretation" && m.sender === "bot").map(m => m.text).join("\n\n");
        if (rawText.trim()) {
          const summaryRes = await axios.post(`${API}/kundli/interpret`, {
            kundli_data: k,
            kundli_data_2: kundliData2 || undefined,
            compatibility: compatibility || undefined,
            question: `Rewrite the following astrological analysis in simple, everyday English that a non-astrologer can understand. Remove all jargon — no house numbers, no planet abbreviations, no Sanskrit terms. Write it as a warm, personal report with clear ## section headers. Keep the key insights but explain simply. Use bullet points for clarity.\n\n${rawText.substring(0, 3000)}`,
            language, fast: true,
          }, { timeout: 60000 });
          if (summaryRes.data.success) clientSummary = summaryRes.data.interpretation;
        }
      } catch { /* use raw reading as fallback */ }
    }

    const dashaRows = (k.dashas || []).map(d =>
      `<tr><td><b>${d.planet}</b></td><td>${d.start_date}</td><td>${d.end_date}</td><td>${d.duration_years}y</td></tr>`
    ).join("");

    let compatHtml = "";
    if (isCompat && compatibility) {
      const c = compatibility;
      const color = c.total_score >= 30 ? "#059669" : c.total_score >= 24 ? "#d97706" : c.total_score >= 18 ? "#ea580c" : "#dc2626";
      compatHtml = `<div class="section no-break" style="text-align:center">
        <h2>Ashta Koot Milan Score</h2>
        <p style="font-size:42px;font-weight:bold;color:${color};margin:8px 0;font-family:Cinzel,serif">${c.total_score}<span style="font-size:18px;color:#888">/36</span></p>
        <p style="font-size:15px;font-weight:bold;color:${color}">${c.verdict} (${c.percentage}%)</p>
        <table style="margin:14px auto;max-width:520px"><tr><th>Factor</th><th>Score</th><th>${isExpert ? "Detail" : "What it means"}</th></tr>
        ${c.koots.map(kk => `<tr><td><b>${kk.name}</b></td><td style="text-align:center"><b>${kk.score}</b>/${kk.max}</td><td>${isExpert ? kk.detail : kk.description}</td></tr>`).join("")}
        </table>
        ${c.nadi_dosha ? '<p style="color:#dc2626;font-weight:bold;margin-top:8px">Nadi Dosha Present — Requires remedial measures</p>' : ""}
      </div>`;
    }

    const displayReading = isExpert
      ? readingHtml
      : (clientSummary || readingHtml || "").replace(/\n/g, "<br>").replace(/## (.*?)(<br>|$)/g, '<h3 style="color:#b8860b;margin:14px 0 4px;font-family:Cinzel,serif">$1</h3>').replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

    // Write the actual report to the already-opened window
    w.document.open();
    w.document.write(`<!DOCTYPE html><html><head><title>Tara Kundli — ${title}</title>
<style>
@import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@500;700&family=Lora:ital,wght@0,400;0,600;1,400&display=swap');
@page { margin: 20mm 16mm; }
* { box-sizing: border-box; }
body { font-family: 'Lora', Georgia, serif; max-width: 720px; margin: 0 auto; padding: 10px 0; color: #1a1a2e; line-height: 1.6; font-size: 12px; }
h1 { font-family: 'Cinzel', serif; color: #b8860b; text-align: center; font-size: 22px; margin: 0 0 2px; }
h2 { font-family: 'Cinzel', serif; color: #4a3728; font-size: 14px; margin: 16px 0 6px; border-bottom: 2px solid #d4a020; padding-bottom: 4px; }
h3 { font-family: 'Cinzel', serif; color: #b8860b; font-size: 12px; margin: 10px 0 4px; }
.cover { text-align: center; padding: 10px 0 8px; }
.subtitle { color: #666; font-size: 11px; margin: 2px 0; }
.report-type { font-family: 'Cinzel', serif; color: #4a3728; font-size: 10px; letter-spacing: 3px; text-transform: uppercase; margin: 6px 0 0; }
.gold-line { height: 2px; background: linear-gradient(90deg, transparent 5%, #d4a020 50%, transparent 95%); margin: 8px 0 12px; }
table { width: 100%; border-collapse: collapse; margin: 6px 0; font-size: 11px; }
th { background: #4a3728; color: #fff; padding: 5px 6px; text-align: left; font-family: 'Cinzel', serif; font-size: 9px; letter-spacing: 0.5px; }
td { border-bottom: 1px solid #e0d8c8; padding: 3px 6px; }
tr:nth-child(even) td { background: #f5f0e8; }
.charts-row { display: flex; gap: 10px; justify-content: center; flex-wrap: wrap; margin: 6px 0; }
.birth-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 3px; }
.birth-grid p { margin: 1px 0; font-size: 11px; }
.birth-grid b { color: #4a3728; }
.reading { line-height: 1.7; font-size: 11.5px; }
.reading p { margin: 3px 0; }
.reading ul, .reading ol { margin: 4px 0; padding-left: 16px; }
.reading li { margin: 2px 0; }
.footer { text-align: center; margin-top: 14px; padding-top: 8px; border-top: 1px solid #ddd; color: #999; font-size: 9px; }
/* Print page breaks — use dedicated page-break elements */
.pb { display: block; height: 0; page-break-after: always; break-after: page; }
</style></head><body>

<div class="cover">
<h1>Tara Kundli</h1>
<p class="subtitle">${isCompat ? "Marriage Compatibility Report" : "Vedic Astrology Report"}</p>
<p class="subtitle">${title}</p>
<p class="report-type">${isExpert ? "Expert Astrologer Edition" : "Personal Consultation Report"}</p>
</div>
<div class="gold-line"></div>

${isCompat ? `
<h2>Man — Birth Details</h2>
<div class="birth-grid">
<p><b>Name:</b> ${name1}</p><p><b>Date:</b> ${k.date_of_birth}</p>
<p><b>Time:</b> ${k.time_of_birth}</p><p><b>Place:</b> ${k.place_of_birth}</p>
${isExpert ? `<p><b>Ascendant:</b> ${k.ascendant?.rashi?.name} (${k.ascendant?.rashi?.hindi})</p><p><b>D-9 Asc:</b> ${k.navamsha?.ascendant?.rashi?.name || "N/A"}</p>` : ""}
</div>

<h2>Woman — Birth Details</h2>
<div class="birth-grid">
<p><b>Name:</b> ${name2}</p><p><b>Date:</b> ${kundliData2.date_of_birth}</p>
<p><b>Time:</b> ${kundliData2.time_of_birth}</p><p><b>Place:</b> ${kundliData2.place_of_birth}</p>
${isExpert ? `<p><b>Ascendant:</b> ${kundliData2.ascendant?.rashi?.name} (${kundliData2.ascendant?.rashi?.hindi})</p><p><b>D-9 Asc:</b> ${kundliData2.navamsha?.ascendant?.rashi?.name || "N/A"}</p>` : ""}
</div>

${compatHtml}

<div class="pb"></div>
<h2>Man's Birth Charts</h2>
${buildPersonCharts(k, name1)}

<div class="pb"></div>
<h2>Woman's Birth Charts</h2>
${buildPersonCharts(kundliData2, name2)}
` : `
<h2>Birth Details</h2>
<div class="birth-grid">
<p><b>Name:</b> ${name1}</p><p><b>Date:</b> ${k.date_of_birth}</p>
<p><b>Time:</b> ${k.time_of_birth}</p><p><b>Place:</b> ${k.place_of_birth}</p>
${isExpert ? `<p><b>D-1 Ascendant:</b> ${k.ascendant?.rashi?.name} (${k.ascendant?.rashi?.hindi}), ${k.ascendant?.degree_string}</p><p><b>D-9 Ascendant:</b> ${k.navamsha?.ascendant?.rashi?.name || "N/A"}</p>` : `<p><b>Ascendant:</b> ${k.ascendant?.rashi?.name}</p>`}
</div>

<h2>${isExpert ? "Kundli Charts" : "Your Birth Charts"}</h2>
${buildPersonCharts(k, name1)}
`}

${isExpert ? `
<div class="pb"></div>
<h2>${isCompat ? "Man — " : ""}Planetary Positions</h2>
${buildPlanetTable(k, true)}
${isCompat && kundliData2 ? `<h2>Woman — Planetary Positions</h2>${buildPlanetTable(kundliData2, true)}` : ""}
<div class="pb"></div>
<h2>${isCompat ? "Man — " : ""}Vimshottari Dasha Periods</h2>
<table><tr><th>Planet</th><th>Start</th><th>End</th><th>Duration</th></tr>${dashaRows}</table>
${isCompat && kundliData2 ? `<div class="pb"></div><h2>Woman — Vimshottari Dasha Periods</h2>
<table><tr><th>Planet</th><th>Start</th><th>End</th><th>Duration</th></tr>${(kundliData2.dashas || []).map(d => `<tr><td><b>${d.planet}</b></td><td>${d.start_date}</td><td>${d.end_date}</td><td>${d.duration_years}y</td></tr>`).join("")}</table>` : ""}
` : ""}

${displayReading ? `<div class="pb"></div><h2>${isExpert ? "Detailed Astrological Analysis" : "Your Personal Reading"}</h2><div class="reading">${displayReading}</div>` : ""}

<div class="footer">
<b>Tara Kundli</b>${isExpert ? " — Swiss Ephemeris | Lahiri Ayanamsa" : ""}<br>
Generated ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
${!isExpert ? `<br>For a deeper, personalized consultation, contact <b>Mamta Dang</b><br>WhatsApp or Call: <b>+1 (437) 422-3693</b>` : ""}
</div></body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 1200);
  };

  const QUICK_QUESTIONS = [
    { icon: Heart, label: "Love Life", q: "Tell me about my love life. When will I find true love? What kind of partner is best for me?" },
    { icon: Sparkles, label: "Marriage", q: "When will I get married? Will my marriage be happy? Are there any delays in marriage?" },
    { icon: Baby, label: "Children", q: "Will I have children? When is the best time? How many children can I expect?" },
    { icon: Briefcase, label: "Career", q: "What career is best for me? Will I get a promotion or job change soon?" },
    { icon: TrendingUp, label: "Finance", q: "Is trading good for me? Should I invest in stocks? When are my best financial periods?" },
    { icon: Home, label: "Property", q: "Should I buy a house or property? When is the best time to invest in real estate?" },
  ];

  const LK_QUICK_QUESTIONS = [
    { icon: Coins, label: "Wealth Remedy", q: "What is my Lal Kitab wealth potential and the single most effective remedy to improve money retention?" },
    { icon: Briefcase, label: "Career", q: "Through Lal Kitab, what career suits me best and which house placements help or block my career growth?" },
    { icon: Heart, label: "Marriage", q: "What does Lal Kitab say about my marriage and married life? Give safe remedies for harmony." },
    { icon: ShieldAlert, label: "Pitra Dosh", q: "Do I have any Pitra Dosh or ancestral karma indicators in Lal Kitab, and what are the corrective upaay?" },
    { icon: Gem, label: "Top Remedy", q: "What is my single most important Lal Kitab remedy right now, and how exactly do I perform it?" },
    { icon: ShieldAlert, label: "Things to Avoid", q: "Based on my Lal Kitab chart, what habits and mistakes should I avoid to prevent aggravating my planets?" },
  ];

  const activeQuickQuestions = mode === "lalkitab" ? LK_QUICK_QUESTIONS : QUICK_QUESTIONS;

  const handleQuickQuestion = (q) => { if (loading) return; addUser(q); askFollowUp(q); };

  const showInput = step === STEPS.CHAT || step === STEPS.RESULTS;

  // --- MODE SELECT ---
  if (step === STEPS.MODE_SELECT) {
    return (
      <div className="flex flex-col items-center justify-center min-h-app-screen px-4 py-8" data-testid="mode-select">
        <Sparkles className="h-10 w-10 text-amber-400 mb-4" />
        <h1 className="font-cinzel text-3xl sm:text-4xl font-bold text-amber-400 tracking-wider mb-2 text-center">Tara Kundli</h1>
        <p className="text-slate-400 text-sm mb-6 sm:mb-10 text-center max-w-md">Vedic Astrology Consultation powered by ancient wisdom</p>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4 max-w-5xl w-full">
          <button onClick={startPersonalFlow} className="glass glow-gold rounded-2xl p-4 sm:p-6 text-left grid grid-cols-[auto_1fr] gap-x-4 items-center sm:block hover:bg-white/5 transition-all duration-300 group" data-testid="mode-personal">
            <User className="h-8 w-8 text-amber-400 row-span-2 sm:mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-cinzel text-amber-400 font-bold text-sm tracking-wide mb-1">Personal Kundli</h3>
            <p className="text-xs text-slate-400 leading-relaxed">Analyze your birth chart, get life predictions, career guidance, and love insights</p>
          </button>
          <button onClick={startCompatFlow} className="glass glow-purple rounded-2xl p-4 sm:p-6 text-left grid grid-cols-[auto_1fr] gap-x-4 items-center sm:block hover:bg-white/5 transition-all duration-300 group" data-testid="mode-compatibility">
            <Users className="h-8 w-8 text-violet-400 row-span-2 sm:mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-cinzel text-violet-400 font-bold text-sm tracking-wide mb-1">Marriage Compatibility</h3>
            <p className="text-xs text-slate-400 leading-relaxed">Match two Kundlis for Gun Milan score, Manglik analysis, and compatibility</p>
          </button>
          <button onClick={startLalKitabFlow} className="glass rounded-2xl p-4 sm:p-6 text-left grid grid-cols-[auto_1fr] gap-x-4 items-center sm:block hover:bg-white/5 transition-all duration-300 group border border-rose-500/20" data-testid="mode-lalkitab">
            <BookOpen className="h-8 w-8 text-rose-400 row-span-2 sm:mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-cinzel text-rose-400 font-bold text-sm tracking-wide mb-1">Lal Kitab Analysis</h3>
            <p className="text-xs text-slate-400 leading-relaxed">House-based Lal Kitab report — karmic patterns, doshas, and safe, practical remedies (upaay)</p>
          </button>
          <button onClick={() => navigate("/tarot")} className="glass rounded-2xl p-4 sm:p-6 text-left grid grid-cols-[auto_1fr] gap-x-4 items-center sm:block hover:bg-white/5 transition-all duration-300 group border border-cyan-500/20" data-testid="mode-tarot">
            <Layers className="h-8 w-8 text-cyan-400 row-span-2 sm:mb-3 group-hover:scale-110 transition-transform" />
            <h3 className="font-cinzel text-cyan-400 font-bold text-sm tracking-wide mb-1">Tarot Reading</h3>
            <p className="text-xs text-slate-400 leading-relaxed">Draw Rider-Waite cards, daily guidance, 3-card spreads, and ask the cards</p>
          </button>
        </div>
      </div>
    );
  }

  // --- CHAT SCREEN ---
  return (
    <div className="flex flex-col h-app-screen" data-testid="chat-page">
      {/* Upgrade prompt overlay */}
      {showUpgradePrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4" data-testid="upgrade-prompt">
          <div className="glass rounded-2xl p-6 max-w-sm w-full border border-amber-500/30 text-center">
            <Sparkles className="h-8 w-8 text-amber-400 mx-auto mb-3" />
            <h3 className="font-cinzel text-lg text-amber-400 font-bold mb-2">Free Readings Used</h3>
            <p className="text-sm text-slate-300 mb-1">Your next free reading is available in:</p>
            <p className="text-2xl font-bold text-amber-400 my-3">{upgradeTimeLeft}</p>
            <p className="text-xs text-slate-500 mb-5">Or purchase additional readings to continue now.</p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowUpgradePrompt(false)}
                className="flex-1 border-slate-500/30 text-slate-400 text-xs">
                I'll wait
              </Button>
              <Button onClick={() => { setShowUpgradePrompt(false); navigate("/upgrade"); }}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-black font-semibold text-xs" data-testid="upgrade-now-btn">
                Get More Readings
              </Button>
            </div>
          </div>
        </div>
      )}

      <ScrollArea className="flex-1 px-4" onWheel={() => { followRef.current = false; }} onTouchMove={() => { followRef.current = false; }}>
        <div className="max-w-3xl mx-auto py-6 space-y-4">
          {messages.map((msg) => (
            <div key={msg.id} className={`animate-slide-up flex ${msg.sender === "user" ? "justify-end" : "justify-start"}`}>
              {msg.type === "loading" ? (
                <div className="bot-bubble rounded-2xl rounded-tl-sm px-5 py-4 max-w-[85%] flex items-center gap-3">
                  <Loader2 className="h-5 w-5 text-amber-400 animate-spin" />
                  <span className="text-slate-300">{msg.text}</span>
                </div>
              ) : msg.type === "results" ? (
                <div className="w-full max-w-[95%]">
                  <div className="bot-bubble rounded-2xl rounded-tl-sm px-5 py-4 mb-3">
                    <p className="text-slate-200 text-sm">{msg.text}</p>
                  </div>
                  <ResultsPanel data={msg.data} data2={kundliData2} mode={mode} />
                  <div className="mt-4 space-y-2">
                    <p className="text-xs text-slate-500 pl-1">Quick questions:</p>
                    <div className="flex flex-wrap gap-2">
                      {activeQuickQuestions.map(qq => (
                        <button key={qq.label} onClick={() => handleQuickQuestion(qq.q)} disabled={loading}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-amber-500/20 text-amber-400/80 bg-amber-500/5 hover:bg-amber-500/15 hover:text-amber-400 hover:border-amber-500/40 transition-all duration-200 disabled:opacity-40"
                          data-testid={`quick-q-${qq.label.toLowerCase().replace(/\s+/g, '-')}`}>
                          <qq.icon className="h-3 w-3" />{qq.label}
                        </button>
                      ))}
                    </div>
                    <div className="mt-3 flex items-center gap-2 text-xs text-slate-600">
                      <div className="h-px flex-1 bg-white/5" />
                      <span>or type your own question below</span>
                      <div className="h-px flex-1 bg-white/5" />
                    </div>
                  </div>
                </div>
              ) : msg.type === "compatibility" ? (
                <div className="w-full max-w-[95%]">
                  <CompatibilityScorecard data={msg.data} />
                  {msg.data?.kundli1 && <ResultsPanel data={msg.data.kundli1} data2={msg.data.kundli2} mode="compatibility" />}
                  <div className="mt-4 space-y-2">
                    <p className="text-xs text-slate-500 pl-1">Ask about the match:</p>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { label: "Full Analysis", q: "Give me a complete compatibility analysis covering love, children, career, and finances for this couple." },
                        { label: "Love & Affection", q: "How is the love and affection compatibility between us? Will we have a happy romantic life?" },
                        { label: "Children", q: "What are the children prospects for this couple? Any concerns about progeny?" },
                        { label: "Career & Money", q: "How will marriage affect our careers and finances? Will we prosper together?" },
                        { label: "Manglik Check", q: "Is there any Manglik Dosha in either chart? What are the remedies?" },
                      ].map(qq => (
                        <button key={qq.label} onClick={() => handleQuickQuestion(qq.q)} disabled={loading}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border border-violet-500/20 text-violet-400/80 bg-violet-500/5 hover:bg-violet-500/15 hover:text-violet-400 hover:border-violet-500/40 transition-all duration-200 disabled:opacity-40"
                          data-testid={`compat-q-${qq.label.toLowerCase().replace(/\s+/g, '-')}`}>
                          {qq.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              ) : msg.type === "growing" || msg.type === "interpretation" ? (
                <LiveAnswer text={msg.text || ""} live={!!msg.live} followRef={followRef}>
                  {msg.type === "growing" && !msg.text && <ThinkingLine />}
                  {msg.type === "growing" && msg.sectionsTotal > 1 && (
                    <div className="flex items-center gap-2 mt-3 pt-3 border-t border-white/5">
                      <div className="flex-1 h-1 bg-white/5 rounded-full overflow-hidden">
                        <div className="h-full bg-amber-500/50 rounded-full transition-all duration-700" style={{width: `${Math.round((msg.sectionsDone / msg.sectionsTotal) * 100)}%`}} />
                      </div>
                      <span className="text-[10px] text-slate-600">{msg.sectionsDone}/{msg.sectionsTotal}</span>
                    </div>
                  )}
                </LiveAnswer>
              ) : msg.sender === "bot" ? (
                <div className="bot-bubble rounded-2xl rounded-tl-sm px-5 py-3.5 max-w-[85%]">
                  {msg.text.split("\n").map((line, i) => <p key={i} className={`text-sm text-slate-200 ${line === "" ? "h-2" : ""}`}>{line}</p>)}
                </div>
              ) : (
                <div className="user-bubble rounded-2xl rounded-tr-sm px-5 py-3.5 max-w-[75%]">
                  <p className="text-sm text-white">{msg.text}</p>
                </div>
              )}
            </div>
          ))}
          {loading && step === STEPS.CHAT && !messages.some(m => m.type === "growing") && (
            <div className="flex justify-start animate-slide-up">
              <div className="bot-bubble rounded-2xl rounded-tl-sm px-5 py-4 max-w-[85%]">
                <div className="flex items-center gap-3 mb-2">
                  <Loader2 className="h-5 w-5 text-amber-400 animate-spin shrink-0" />
                  <span className="text-sm text-slate-300 font-medium">Analyzing your chart...</span>
                </div>
                <div className="space-y-1.5 ml-8">
                  <p className="text-xs text-slate-500">Checking D-1 Rashi + D-9 Navamsha positions, Dasha periods, aspects, and transits</p>
                  <div className="flex items-center gap-2">
                    <div className="h-1 flex-1 bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-500/40 rounded-full animate-pulse" style={{width: "60%"}} />
                    </div>
                    <span className="text-[10px] text-slate-600">~30-45 sec</span>
                  </div>
                </div>
              </div>
            </div>
          )}
          {(step === STEPS.FORM || step === STEPS.FORM2) && (
            <div className="flex justify-start animate-slide-up">
              <BirthDetailsForm
                title={step === STEPS.FORM2 ? "Woman's Birth Details" : (mode === "compatibility" ? "Man's Birth Details" : "Enter Birth Details")}
                onSubmit={step === STEPS.FORM2 ? handleForm2Submit : handleFormSubmit}
                loading={loading}
              />
            </div>
          )}
          <div ref={endRef} />
        </div>
      </ScrollArea>

      {/* Prominent chat bar */}
      {(showInput || (kundliData && (step === STEPS.CHAT || step === STEPS.RESULTS))) && (
        <div className="border-t-2 border-amber-500/30 bg-slate-950/95 backdrop-blur-lg p-4 shadow-[0_-4px_20px_rgba(0,0,0,0.4)]" data-testid="chat-bar">
          <div className="max-w-3xl mx-auto space-y-3">
            {kundliData && (
              <div className="flex gap-2 flex-wrap" data-testid="action-buttons">
                <Button variant="outline" size="sm" onClick={handleSaveSession} disabled={saved}
                  className={`text-xs gap-1.5 ${saved ? "border-emerald-500/30 text-emerald-400" : "border-amber-500/30 text-amber-400 hover:bg-amber-500/10"}`}
                  data-testid="save-session-btn">
                  <Save className="h-3.5 w-3.5" />{saved ? "Saved" : "Save"}
                </Button>
                <div className="relative">
                  <Button variant="outline" size="sm" onClick={() => setShowExportMenu(!showExportMenu)}
                    className="text-xs gap-1.5 border-violet-500/30 text-violet-400 hover:bg-violet-500/10"
                    data-testid="export-pdf-btn">
                    <FileDown className="h-3.5 w-3.5" />Export PDF
                  </Button>
                  {showExportMenu && (
                    <div className="absolute bottom-full mb-1 left-0 w-48 bg-slate-900 border border-white/10 rounded-lg shadow-xl overflow-hidden z-50">
                      <button onClick={() => handleExportPDF("expert")}
                        className="w-full text-left px-3 py-2.5 text-xs text-slate-200 hover:bg-amber-500/10 hover:text-amber-400 transition-colors border-b border-white/5"
                        data-testid="export-expert">
                        <span className="font-semibold block">Expert Report</span>
                        <span className="text-[10px] text-slate-500">For astrologers — full technical detail</span>
                      </button>
                      <button onClick={() => handleExportPDF("client")}
                        className="w-full text-left px-3 py-2.5 text-xs text-slate-200 hover:bg-violet-500/10 hover:text-violet-400 transition-colors"
                        data-testid="export-client">
                        <span className="font-semibold block">Client Report</span>
                        <span className="text-[10px] text-slate-500">Simplified — for sharing with clients</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
            <div className="flex gap-3 items-center">
              <Input ref={inputRef} value={inputValue} onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && handleSend()}
                placeholder={mode === "lalkitab" ? "Ask about your Lal Kitab chart, doshas, or remedies (upaay)..." : "Ask about love, marriage, career, finances, health..."}
                disabled={loading}
                className="flex-1 bg-white/5 border-amber-500/25 text-slate-100 placeholder:text-slate-400 h-12 text-sm focus-visible:ring-amber-500/50 focus-visible:border-amber-500/40"
                data-testid="chat-input" />
              <Button onClick={handleSend} disabled={loading || !inputValue.trim()}
                className="bg-amber-500 hover:bg-amber-600 text-black h-12 px-6 shrink-0 font-bold" data-testid="send-button">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CompatibilityScorecard({ data }) {
  if (!data) return null;
  const c = data.compatibility;
  if (!c) return (
    <div className="glass rounded-2xl p-5 mb-4 border border-violet-500/20 flex items-center justify-center gap-2">
      <Loader2 className="h-4 w-4 text-violet-400 animate-spin" />
      <p className="text-sm text-slate-400">Loading compatibility scores...</p>
    </div>
  );
  const scoreColor = c.total_score >= 30 ? "text-emerald-400" : c.total_score >= 24 ? "text-amber-400" : c.total_score >= 18 ? "text-orange-400" : "text-red-400";
  const scoreBg = c.total_score >= 30 ? "bg-emerald-500/10 border-emerald-500/30" : c.total_score >= 24 ? "bg-amber-500/10 border-amber-500/30" : c.total_score >= 18 ? "bg-orange-500/10 border-orange-500/30" : "bg-red-500/10 border-red-500/30";

  return (
    <div className="glass rounded-2xl p-5 mb-4 border border-violet-500/20" data-testid="compatibility-scorecard">
      <h2 className="font-cinzel text-violet-400 text-lg font-bold tracking-wide mb-1">Ashta Koot Milan</h2>
      <p className="text-xs text-slate-500 mb-4">{data.kundli1?.name} & {data.kundli2?.name}</p>

      {/* Total Score */}
      <div className={`rounded-xl p-4 mb-4 border text-center ${scoreBg}`}>
        <p className={`font-cinzel text-4xl font-bold ${scoreColor}`}>{c.total_score}<span className="text-lg text-slate-500">/36</span></p>
        <p className={`text-sm font-semibold mt-1 ${scoreColor}`}>{c.verdict}</p>
        <p className="text-xs text-slate-500 mt-1">{c.percentage}% compatibility</p>
      </div>

      {/* Individual Koots */}
      <div className="space-y-2">
        {c.koots?.map((koot) => {
          const pct = koot.max > 0 ? (koot.score / koot.max) * 100 : 0;
          const barColor = pct >= 75 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : pct > 0 ? "bg-orange-500" : "bg-red-500";
          return (
            <div key={koot.name} className="flex items-center gap-3" data-testid={`koot-${koot.name.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="w-24 shrink-0">
                <p className="text-xs font-semibold text-slate-300">{koot.name}</p>
                <p className="text-[10px] text-slate-600">{koot.description}</p>
              </div>
              <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                <div className={`h-full rounded-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
              </div>
              <span className="text-xs font-bold text-slate-300 w-10 text-right">{koot.score}/{koot.max}</span>
            </div>
          );
        })}
      </div>

      {/* Nadi Dosha warning */}
      {c.nadi_dosha && (
        <div className="mt-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
          <p className="text-xs text-red-400 font-semibold">Nadi Dosha Detected</p>
          <p className="text-[10px] text-red-400/70 mt-0.5">Same Nadi for both partners — may affect progeny health. Remedies recommended.</p>
        </div>
      )}

      <p className="text-[10px] text-slate-600 mt-3 text-center">
        Moon Nakshatras: {c.boy_nakshatra} & {c.girl_nakshatra} | Rashis: {c.boy_rashi} & {c.girl_rashi}
      </p>
    </div>
  );
}

function ResultsPanel({ data, data2, mode }) {
  if (!data) return null;
  const isCompat = mode === "compatibility";
  return (
    <div className="glass rounded-2xl p-4 glow-gold" data-testid="results-panel">
      <div className="mb-4">
        <h2 className="font-cinzel text-amber-400 text-lg font-bold tracking-wide">
          {isCompat ? `${data.name} & ${data2?.name || "Partner"}` : `${data.name}'s Kundli`}
        </h2>
        <p className="text-xs text-slate-500 font-data mt-0.5">
          {data.date_of_birth} | {data.time_of_birth} | {data.place_of_birth}
        </p>
      </div>
      <Tabs defaultValue="chart" className="w-full">
        <TabsList className="w-full bg-black/30 border border-white/5 mb-4 p-1">
          <TabsTrigger value="chart" className="flex-1 text-xs data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-400" data-testid="tab-chart">Chart</TabsTrigger>
          <TabsTrigger value="navamsha" className="flex-1 text-xs data-[state=active]:bg-violet-500/15 data-[state=active]:text-violet-400" data-testid="tab-navamsha">Navamsha</TabsTrigger>
          <TabsTrigger value="planets" className="flex-1 text-xs data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-400" data-testid="tab-planets">Planets</TabsTrigger>
          <TabsTrigger value="dashas" className="flex-1 text-xs data-[state=active]:bg-amber-500/15 data-[state=active]:text-amber-400" data-testid="tab-dashas">Dashas</TabsTrigger>
        </TabsList>
        <TabsContent value="chart" className="mt-0">
          {isCompat && <p className="text-xs text-amber-400/70 font-cinzel tracking-wide mb-2">Man — {data.name}</p>}
          <NorthIndianChart houses={data.houses} />
          {data2 && <><p className="text-xs text-amber-400/70 mt-4 mb-2 font-cinzel">Woman — {data2.name}</p><NorthIndianChart houses={data2.houses} /></>}
        </TabsContent>
        <TabsContent value="navamsha" className="mt-0">
          {isCompat && <p className="text-xs text-violet-400 font-cinzel tracking-wide mb-2">Man — {data.name} (D-9)</p>}
          {!isCompat && <p className="text-xs text-violet-400 font-cinzel tracking-wide mb-2">Navamsha (D-9) — Marriage & Inner Strength</p>}
          {data.navamsha?.houses ? (
            <>
              <NorthIndianChart houses={data.navamsha.houses} />
              {data2?.navamsha?.houses && <><p className="text-xs text-violet-400 mt-4 mb-2 font-cinzel">Woman — {data2.name} (D-9)</p><NorthIndianChart houses={data2.navamsha.houses} /></>}
            </>
          ) : (
            <p className="text-sm text-slate-500 py-8 text-center">Navamsha data not available.</p>
          )}
        </TabsContent>
        <TabsContent value="planets" className="mt-0">
          {isCompat && <p className="text-xs text-amber-400/70 font-cinzel tracking-wide mb-2">Man — {data.name}</p>}
          <PlanetaryPositions planets={data.planets} ascendant={data.ascendant} houses={data.houses} />
          {data2 && <>
            <p className="text-xs text-amber-400/70 mt-4 mb-2 font-cinzel">Woman — {data2.name}</p>
            <PlanetaryPositions planets={data2.planets} ascendant={data2.ascendant} houses={data2.houses} />
          </>}
        </TabsContent>
        <TabsContent value="dashas" className="mt-0">
          {isCompat && <p className="text-xs text-amber-400/70 font-cinzel tracking-wide mb-2">Man — {data.name}</p>}
          <DashaPeriods dashas={data.dashas} />
          {data2 && <>
            <p className="text-xs text-amber-400/70 mt-4 mb-2 font-cinzel">Woman — {data2.name}</p>
            <DashaPeriods dashas={data2.dashas} />
          </>}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function BirthDetailsForm({ title, onSubmit, loading }) {
  const [name, setName] = useState("");
  const [dob, setDob] = useState("");
  const [tob, setTob] = useState("");
  const [place, setPlace] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [selectedCoords, setSelectedCoords] = useState(null);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  const searchPlaces = async (query) => {
    if (query.length < 2) { setSuggestions([]); return; }
    setSearching(true);
    try {
      const res = await axios.get(`${API}/places/search`, { params: { q: query } });
      setSuggestions(res.data.results || []);
    } catch {
      setSuggestions([]);
    } finally {
      setSearching(false);
    }
  };

  const handlePlaceChange = (val) => {
    setPlace(val);
    setSelectedCoords(null);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchPlaces(val), 250);
  };

  const handleSelectPlace = async (item) => {
    setPlace(item.display || item.name);
    setSuggestions([]);
    // If already has lat/lon (from cache), use directly
    if (item.lat != null && item.lon != null) {
      setSelectedCoords({ lat: item.lat, lon: item.lon, tz: item.tz });
      return;
    }
    // Google Place — resolve placeId to get lat/lon
    if (item.place_id) {
      setSearching(true);
      try {
        const res = await axios.get(`${API}/places/details`, { params: { place_id: item.place_id } });
        if (res.data.success) {
          setSelectedCoords({ lat: res.data.lat, lon: res.data.lon, tz: res.data.tz });
        }
      } catch { /* fallback: backend will geocode from name */ }
      finally { setSearching(false); }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim() || !dob || !tob || !place.trim()) return;
    const [y, m, d] = dob.split("-");
    const formattedDob = `${d}-${m}-${y}`;
    const [hh, mm] = tob.split(":");
    const h = parseInt(hh);
    const ampm = h >= 12 ? "PM" : "AM";
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    const formattedTob = `${h12}:${mm} ${ampm}`;

    onSubmit({
      name: name.trim(),
      date_of_birth: formattedDob,
      time_of_birth: formattedTob,
      place_of_birth: place.trim(),
      latitude: selectedCoords?.lat || null,
      longitude: selectedCoords?.lon || null,
      timezone_str: selectedCoords?.tz || null,
    });
  };

  return (
    <div className="w-full max-w-md" data-testid="birth-details-form">
      <form onSubmit={handleSubmit} className="glass rounded-2xl p-5 glow-gold space-y-4">
        <h3 className="font-cinzel text-amber-400 font-bold text-sm tracking-wide">{title}</h3>

        <div className="space-y-1.5">
          <label className="text-xs text-slate-400 font-medium">Full Name</label>
          <input type="text" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Rahul Sharma"
            className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-slate-100 text-sm placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-amber-500/50"
            data-testid="form-name" required />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs text-slate-400 font-medium">Date of Birth</label>
          <input type="date" value={dob} onChange={(e) => setDob(e.target.value)}
            className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-slate-100 text-sm focus:outline-none focus:ring-1 focus:ring-amber-500/50 [color-scheme:dark]"
            data-testid="form-dob" required />
        </div>

        <div className="space-y-1.5">
          <label className="text-xs text-slate-400 font-medium">Time of Birth</label>
          <input type="time" value={tob} onChange={(e) => setTob(e.target.value)}
            className="w-full h-10 px-3 rounded-lg bg-white/5 border border-white/10 text-slate-100 text-sm focus:outline-none focus:ring-1 focus:ring-amber-500/50 [color-scheme:dark]"
            data-testid="form-tob" required />
        </div>

        <div className="space-y-1.5 relative">
          <label className="text-xs text-slate-400 font-medium flex items-center gap-1">
            <MapPin className="h-3 w-3" /> Place of Birth
          </label>
          <div className="relative">
            <input type="text" value={place} onChange={(e) => handlePlaceChange(e.target.value)}
              placeholder="Search any city worldwide..."
              className="w-full h-10 px-3 pr-8 rounded-lg bg-white/5 border border-white/10 text-slate-100 text-sm placeholder:text-slate-500 focus:outline-none focus:ring-1 focus:ring-amber-500/50"
              data-testid="form-place" required autoComplete="off" />
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              {searching ? (
                <Loader2 className="h-3.5 w-3.5 text-slate-500 animate-spin" />
              ) : selectedCoords ? (
                <div className="w-2 h-2 rounded-full bg-emerald-400" title="Location verified" />
              ) : null}
            </div>
          </div>
          {suggestions.length > 0 && (
            <div className="absolute z-50 w-full mt-1 rounded-lg bg-slate-900 border border-white/10 shadow-xl overflow-hidden max-h-48 overflow-y-auto">
              {suggestions.map((item, idx) => (
                <button key={idx} type="button"
                  className="w-full text-left px-3 py-2.5 text-sm text-slate-200 hover:bg-amber-500/10 hover:text-amber-400 transition-colors border-b border-white/5 last:border-0 flex items-start gap-2"
                  onClick={() => handleSelectPlace(item)}
                  data-testid={`place-suggestion-${idx}`}>
                  <MapPin className="h-3.5 w-3.5 text-slate-500 mt-0.5 shrink-0" />
                  <span>{item.display || item.name}</span>
                </button>
              ))}
            </div>
          )}
          <p className="text-[10px] text-slate-600 mt-1">Type to search — select for best accuracy</p>
        </div>

        <Button type="submit" disabled={loading || !name.trim() || !dob || !tob || !place.trim()}
          className="w-full bg-amber-500 hover:bg-amber-600 text-black h-11 font-semibold gap-2"
          data-testid="form-submit-btn">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
          Generate Kundli
        </Button>
      </form>
    </div>
  );
}
